import React, { useMemo, useState } from 'react'
import { IoCallOutline } from 'react-icons/io5'
import useSWR from 'swr'
import useAuth from '../../context/userContext'
import api, { fetchAll } from '../../utils/api'
import { ErrorNotification, SuccessNotification } from '../../utils'
import Spinner from '../Spinner'
import {
  fetchAssignedVoiceCallerNumber,
  formatVoiceCallerApiError,
  getVoiceCallerRejectionNotes,
  getVoiceCallerRequestNumber,
  getVoiceCallerRequestsKey,
  hasAssignedPrivateVoiceNumber,
  hasPendingVoiceCallerRequest,
  isEligibleForPrivateNumberRequest,
  normalizeVoiceCallerRequests,
  PLATFORM_DEFAULT_VOICE_CALLER_LABEL,
} from '../../utils/voiceCallerNumber'

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
]

const statusStyles = {
  pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
  approved: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
}

const formatDate = (value) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const RequestPrivateNumberSection = () => {
  const { user } = useAuth()
  const [statusFilter, setStatusFilter] = useState('all')
  const [submitting, setSubmitting] = useState(false)

  const canRequest = isEligibleForPrivateNumberRequest(user)

  const {
    data: allRequestsData,
    mutate: mutateAllRequests,
  } = useSWR(canRequest ? 'voice-caller-number-requests/' : null, fetchAll)

  const {
    data: filteredRequestsData,
    isLoading,
    mutate: mutateFilteredRequests,
  } = useSWR(
    canRequest ? getVoiceCallerRequestsKey(statusFilter) : null,
    fetchAll
  )

  const {
    data: assignedNumberData,
    isLoading: loadingAssignedNumber,
    mutate: mutateAssignedNumber,
  } = useSWR(
    canRequest ? 'voice-caller-numbers/mine/' : null,
    fetchAssignedVoiceCallerNumber
  )

  const allRequests = useMemo(
    () => normalizeVoiceCallerRequests(allRequestsData),
    [allRequestsData]
  )

  const requests = useMemo(
    () => normalizeVoiceCallerRequests(filteredRequestsData),
    [filteredRequestsData]
  )

  const assignedNumber = assignedNumberData || null
  const hasPendingRequest = hasPendingVoiceCallerRequest(allRequests)
  const hasPrivateNumber = hasAssignedPrivateVoiceNumber(assignedNumber)

  const refreshPrivateNumberData = () => {
    mutateAllRequests()
    mutateFilteredRequests()
    mutateAssignedNumber()
  }

  const handleSubmitRequest = async () => {
    if (!canRequest) {
      ErrorNotification('Only service providers can request a private voice caller number.')
      return
    }

    if (hasPendingRequest) {
      ErrorNotification('You already have a pending private number request.')
      return
    }

    setSubmitting(true)
    try {
      await api.post('voice-caller-number-requests/')
      SuccessNotification('Private number request submitted successfully.')
      refreshPrivateNumberData()
    } catch (error) {
      ErrorNotification(formatVoiceCallerApiError(error, 'Failed to submit private number request.'))
    } finally {
      setSubmitting(false)
    }
  }

  if (!canRequest) {
    return (
      <div className="border dark:border-border-dark border-gray-200 rounded-lg bg-gray-50 dark:bg-dark p-6">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Private voice caller numbers are available for service provider accounts only.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="border dark:border-border-dark border-gray-200 rounded-lg p-4 bg-gray-50 dark:bg-dark space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1">
              Active Voice Caller ID
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              The number used for voice campaigns after a request is approved.
            </p>
            {loadingAssignedNumber ? (
              <div className="flex items-center gap-2 py-1">
                <Spinner />
                <span className="text-sm text-gray-500 dark:text-gray-400">Loading assigned number...</span>
              </div>
            ) : hasPrivateNumber ? (
              <span className="font-semibold text-base dark:text-white">{assignedNumber.number}</span>
            ) : (
              <span className="font-semibold text-base dark:text-white">{PLATFORM_DEFAULT_VOICE_CALLER_LABEL}</span>
            )}
          </div>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${
            hasPrivateNumber
              ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
              : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
          }`}>
            {hasPrivateNumber ? 'Private' : 'Platform'}
          </span>
        </div>
        {!loadingAssignedNumber && !hasPrivateNumber && assignedNumber?.detail && (
          <p className="text-xs text-gray-500 dark:text-gray-400 border-t dark:border-border-dark border-gray-200 pt-3">
            {assignedNumber.detail}
          </p>
        )}
      </div>

      <div className="border dark:border-border-dark border-gray-200 rounded-lg bg-white dark:bg-secondaryDark p-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 bg-yellowcolor/20 rounded-full flex items-center justify-center shrink-0">
            <IoCallOutline className="text-yellowcolor text-xl" />
          </div>
          <div className="flex-1">
            <h2 className="text-base font-bold dark:text-white">Request a Private Number</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Submit a request for a dedicated caller ID. An administrator will review and assign a phone number if approved.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {hasPrivateNumber
              ? 'You already have a private voice caller number assigned.'
              : hasPendingRequest
                ? 'Your private number request is awaiting approval.'
                : 'No private number assigned yet.'}
          </p>
          <button
            type="button"
            onClick={handleSubmitRequest}
            // disabled={submitting || hasPendingRequest || hasPrivateNumber}
            disabled={true}
            className="px-6 py-2 bg-yellowcolor hover:bg-yellowcolor/90 text-black rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          >
            {/* {submitting ? 'Submitting...' : 'Request Private Number'} */}
            Coming Soon
          </button>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            Your Private Number Requests
          </h2>
          <div className="flex flex-row flex-wrap border dark:border-border-dark w-fit rounded-md overflow-hidden">
            {STATUS_FILTERS.map((filter) => (
              <button
                key={filter.key}
                type="button"
                onClick={() => setStatusFilter(filter.key)}
                className={`px-4 py-1.5 text-sm border dark:border-border-dark transition-all duration-150 ${
                  statusFilter === filter.key
                    ? 'bg-white text-black dark:text-white border-gray-200 dark:bg-black'
                    : 'bg-gray-100 text-gray-500 border-gray-100 dark:bg-secondaryDark'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner />
          </div>
        ) : requests.length > 0 ? (
          <div className="space-y-3">
            {requests.map((request) => {
              const status = (request?.status || 'pending').toLowerCase()
              const number = getVoiceCallerRequestNumber(request)
              const rejectionNotes = getVoiceCallerRejectionNotes(request)

              return (
                <div
                  key={request.id}
                  className="border dark:border-border-dark border-gray-200 rounded-lg p-4 bg-white dark:bg-secondaryDark"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-semibold text-base dark:text-white">
                          {number || 'Awaiting number assignment'}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                            statusStyles[status] || statusStyles.pending
                          }`}
                        >
                          {status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Requested on {formatDate(request?.created_at || request?.datecreated)}
                      </p>
                      {status === 'rejected' && rejectionNotes && (
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                          Reason: {rejectionNotes}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="text-center py-6 border dark:border-border-dark border-gray-200 rounded-lg bg-gray-50 dark:bg-dark">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No private number requests found{statusFilter !== 'all' ? ` with status "${statusFilter}"` : ''}.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default RequestPrivateNumberSection
